import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Rating,
  Paper,
  CardMedia,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Close,
  Restaurant,
  DeliveryDining,
  ShoppingBag,
  Star,
  LocalOffer,
  AccessTime,
  Favorite,
  FavoriteBorder,
  Share,
  Add,
  Remove,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ViewDetailsModal = ({ open, onClose, item }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!item) return null;

  const handleOrderNow = (serviceType) => {
    onClose();
    navigate(`/${serviceType}`, { state: { selectedItem: item } });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100vh' : '90vh',
          height: isMobile ? '100%' : 'auto',
          margin: isMobile ? 0 : 1,
          boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogContent sx={{ 
        p: 0, 
        position: 'relative',
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header with Image */}
        <Box sx={{ position: 'relative', height: { xs: 160, sm: 180, md: 200 } }}>
          <CardMedia
            component="img"
            image={item.image_url}
            alt={item.name}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          
          {/* Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)',
            }}
          />
          
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              '&:hover': { 
                bgcolor: 'rgba(255, 255, 255, 1)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <Close />
          </IconButton>

          {/* Action Buttons */}
          <Box sx={{ position: 'absolute', top: 16, left: 16, display: 'flex', gap: 1 }}>
            <IconButton
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                '&:hover': { 
                  bgcolor: 'rgba(255, 255, 255, 1)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <FavoriteBorder />
            </IconButton>
            <IconButton
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                '&:hover': { 
                  bgcolor: 'rgba(255, 255, 255, 1)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Share />
            </IconButton>
          </Box>

          {/* Title Overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 3,
              color: 'white',
            }}
          >
            <Fade in={open} timeout={800}>
              <Box>
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  fontWeight="800"
                  sx={{
                    textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                    mb: 1,
                    fontFamily: '"Playfair Display", "Georgia", serif',
                  }}
                >
                  {item.name}
                </Typography>
                
                {/* Rating and Price */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating 
                      value={item.rating || 4.5} 
                      precision={0.1} 
                      readOnly 
                      size="small"
                      sx={{ '& .MuiRating-icon': { color: '#ffd700' } }}
                    />
                    <Typography variant="body2" sx={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                      ({item.rating || 4.5})
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                      {item.preparation_time || 15} min
                    </Typography>
                  </Box>
                </Box>

                {/* Price */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {item.discount_enabled && item.discount_percentage > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          textDecoration: 'line-through', 
                          opacity: 0.7,
                          textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                        }}
                      >
                        S${item.base_price}
                      </Typography>
                      <Chip
                        label={`${item.discount_percentage}% OFF`}
                        size="small"
                        sx={{ 
                          bgcolor: '#ff4444',
                          color: 'white',
                          fontWeight: 600,
                          textShadow: 'none',
                        }}
                      />
                    </Box>
                  ) : null}
                  <Typography 
                    variant="h4" 
                    fontWeight="700"
                    sx={{
                      textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                      background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    S${item.discount_enabled && item.discount_percentage > 0 
                      ? (item.base_price * (1 - item.discount_percentage / 100)).toFixed(2)
                      : item.base_price}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 },
          flex: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#ccc',
            borderRadius: '2px',
          },
        }}>
          {/* Description */}
          <Fade in={open} timeout={1000}>
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  lineHeight: 1.5,
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                {item.description || 'No description available'}
              </Typography>
            </Box>
          </Fade>

          {/* Sizes */}
          {item.sizes_with_prices && item.sizes_with_prices.length > 0 && (
            <Zoom in={open} timeout={1200}>
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="subtitle1" 
                  fontWeight="600" 
                  sx={{ 
                    mb: 1.5,
                    fontFamily: '"Playfair Display", "Georgia", serif',
                    color: 'text.primary',
                  }}
                >
                  Available Sizes
                </Typography>
                <Grid container spacing={1}>
                  {item.sizes_with_prices.map((size) => (
                    <Grid item xs={6} sm={4} key={size.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: size.is_default ? 2 : 1,
                          borderColor: size.is_default ? 'primary.main' : 'divider',
                          bgcolor: size.is_default ? 'primary.50' : 'background.paper',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3,
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 0.5 }}>
                            {size.name}
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color="primary" 
                            fontWeight="700"
                          >
                            S${size.final_price}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Zoom>
          )}

          {/* Addons */}
          {item.addons_with_details && item.addons_with_details.length > 0 && (
            <Zoom in={open} timeout={1400}>
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="subtitle1" 
                  fontWeight="600" 
                  sx={{ 
                    mb: 1.5,
                    fontFamily: '"Playfair Display", "Georgia", serif',
                    color: 'text.primary',
                  }}
                >
                  Available Add-ons
                </Typography>
                <Grid container spacing={1}>
                  {item.addons_with_details.map((addon) => (
                    <Grid item xs={12} sm={6} key={addon.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 0.5 }}>
                            {addon.name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ mb: 1, fontSize: '0.85rem' }}
                          >
                            {addon.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" color="primary" fontWeight="700">
                              S${addon.price}
                            </Typography>
                            {addon.is_required && (
                              <Chip
                                label="Required"
                                size="small"
                                color="error"
                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Zoom>
          )}

          {/* Order Options */}
          <Fade in={open} timeout={1600}>
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography 
                variant="h6" 
                fontWeight="600" 
                textAlign="center"
                sx={{ 
                  mb: 2,
                  fontFamily: '"Playfair Display", "Georgia", serif',
                  background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                How would you like to order?
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<DeliveryDining />}
                    onClick={() => handleOrderNow('delivery')}
                    sx={{
                      height: { xs: 50, sm: 55 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      fontWeight: 600,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #45a049 0%, #4CAF50 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Delivery
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<ShoppingBag />}
                    onClick={() => handleOrderNow('takeaway')}
                    sx={{
                      height: { xs: 50, sm: 55 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      fontWeight: 600,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                      boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #F57C00 0%, #FF9800 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 16px rgba(255, 152, 0, 0.4)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Takeaway
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Restaurant />}
                    onClick={() => handleOrderNow('table-order')}
                    sx={{
                      height: { xs: 50, sm: 55 },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      fontWeight: 600,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                      boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1976D2 0%, #2196F3 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Dine In
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ViewDetailsModal;
